// Admin-only user management card (Settings): add users, reset passwords,
// deactivate/reactivate. All writes go through the export service's /admin
// endpoints, which re-verify the caller's token and admin+active flags.
import { useEffect, useState } from "react";
import { pb, ADD_USER_URL, DEACTIVATE_URL } from "./pb";
import { canDeactivate, isValidEmail, mapAdminError } from "./calc";

export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(null); // user record pending deactivation
  const callerId = pb.authStore.model?.id;

  const load = () => pb.collection("users").getFullList({ sort: "email" })
    .then(setUsers).catch(() => setMsg("Couldn't load users."));
  useEffect(() => { load(); }, []);

  async function post(url, body) {
    const r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": pb.authStore.token },
      body: JSON.stringify(body),
    });
    if (!r.ok) {
      const err = new Error(`Admin request failed: ${r.status}`);
      err.status = r.status;
      throw err;
    }
    return r.json();
  }

  async function addUser() {
    if (!isValidEmail(email)) { setMsg("Enter a valid email."); return; }
    setBusy(true); setMsg("");
    try { await post(ADD_USER_URL, { email: email.trim().toLowerCase() });
      setMsg("Invite sent — they'll get an email to set their password."); setEmail(""); load(); }
    catch (err) { setMsg(mapAdminError(err.status || 500)); }
    finally { setBusy(false); }
  }

  async function toggleActive(u) {
    if (!canDeactivate(callerId, u.id)) return;
    if (u.active) { setConfirmDeactivate(u); return; } // confirm before blocking access
    setBusy(true); setMsg("");
    try { await post(DEACTIVATE_URL, { userId: u.id, active: true });
      setMsg(`${u.email} can sign in again.`); load(); }
    catch (err) { setMsg(mapAdminError(err.status || 500)); }
    finally { setBusy(false); }
  }

  async function confirmDeactivateNow() {
    const u = confirmDeactivate;
    setConfirmDeactivate(null);
    setBusy(true); setMsg("");
    try { await post(DEACTIVATE_URL, { userId: u.id, active: false });
      setMsg(`${u.email} has been deactivated — they can no longer sign in. Their data is kept.`); load(); }
    catch (err) { setMsg(mapAdminError(err.status || 500)); }
    finally { setBusy(false); }
  }

  function sendReset(u) {
    pb.collection("users").requestPasswordReset(u.email).catch(() => {});
    setMsg(`Password-reset email sent to ${u.email} (if the account exists).`);
  }

  return (
    <div className="f-card" style={{ margin: "0 16px 4px" }}>
      <div className="f-sec" style={{ padding: "14px 16px 0" }}>Users</div>
      <div className="blk-hint" style={{ padding: "0 16px 10px" }}>
        <strong>Reset password</strong> emails them a link to set a new password — use if they're locked out.{" "}
        <strong>Deactivate</strong> blocks sign-in without deleting their data — use to remove access (e.g. they left).{" "}
        <strong>Activate</strong> restores sign-in for a deactivated account.
      </div>
      <div className="f-row" style={{ gap: 8 }}>
        <input className="f-other-sel" style={{ position: "static", opacity: 1, flex: 1 }}
          type="email" placeholder="coworker@email" value={email}
          onChange={e => setEmail(e.target.value)} aria-label="New user email" />
        <button className="a-fbtn save" disabled={busy} onClick={addUser}>Add</button>
      </div>
      {users.map(u => (
        <div key={u.id} className="f-row">
          <span className="f-val">
            <span className="tp-dot" style={{ background: u.active ? "var(--money)" : "var(--ink-3)" }}
              title={u.active ? "Active — can sign in" : "Deactivated — can't sign in"} />
            {u.email}
          </span>
          <span style={{ display: "flex", gap: 12 }}>
            <button className="a-fbtn" title="Email them a password-reset link" onClick={() => sendReset(u)}>Reset password</button>
            {canDeactivate(callerId, u.id) && (
              <button className="a-fbtn" style={{ color: u.active ? "var(--fire)" : "var(--accent)" }}
                title={u.active ? "Block this account from signing in (data is kept)" : "Allow this account to sign in again"}
                disabled={busy} onClick={() => toggleActive(u)}>
                {u.active ? "Deactivate" : "Activate"}
              </button>
            )}
          </span>
        </div>
      ))}
      {msg && <div className="blk-hint">{msg}</div>}

      {confirmDeactivate && (
        <div className="modal-bg">
          <div className="modal" style={{ maxWidth: 360 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "var(--text)", marginBottom: 8 }}>Deactivate {confirmDeactivate.email}?</h2>
            <p style={{ fontSize: 13, color: "var(--text2)", lineHeight: 1.6, marginBottom: 24 }}>
              They'll be blocked from signing in immediately. Their entries and expenses are kept — you can Activate them again any time.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn-ghost" onClick={() => setConfirmDeactivate(null)}>Cancel</button>
              <button className="btn-primary" style={{ background: "#cc3333", color: "#fff" }} onClick={confirmDeactivateNow}>Deactivate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
